<template>
  <FlotoFixedView>
    <FlotoPageHeader class="px-4" use-divider>
      <template v-slot:title>
        <span>
          <FlotoDot
            v-if="agentStatus"
            :bg="agentStatus"
            class="cursor-pointer mr-1"
            :size="15"
          />
          {{
            moduleName === $constants.CMDB ? resource.ciName : resource.name
          }}:
          {{
            moduleName === $constants.CMDB
              ? resource.ciDisplayName
              : resource.displayName
          }}
        </span>
      </template>
      <template v-slot:back-button>
        <slot name="before-title"></slot>
      </template>
      <template v-slot:title-append>
        <MTooltip v-if="!disabled">
          <template v-slot:trigger>
            <MButton
              variant="neutral-light"
              :shadow="false"
              shape="circle"
              class="text-neutral md-text-base mx-2"
              @click="handleEditSubject"
            >
              <MIcon name="pencil" /> </MButton
          ></template>
          {{ $t('edit') }}
        </MTooltip>
      </template>
      <slot name="additional-header-content" />
      <template v-slot:after-title>
        <slot name="actions" />
      </template>
    </FlotoPageHeader>
    <FlotoScrollView>
      <MCol :size="12" class="px-6 flex flex-col" :style="defaultStyle">
        <MRow class="flex-no-wrap flex-1">
          <MCol
            class="transition-width main-detail-container"
            v-bind="detailSize"
          >
            <slot
              v-if="
                resource.description &&
                (!allowedFields || allowedFields.indexOf('description') >= 0)
              "
              name="description"
            >
              <div>
                <h6 class="text-primary">
                  {{ $t('description') }}
                </h6>
              </div>
              <div class="description mb-2 mr-4">
                <FlotoReadMore :height="20 * 3">
                  <FlotoRichEditor :value="resource.description" disabled />
                </FlotoReadMore>
                <MDivider />
              </div>
            </slot>
            <slot name="primary-row">
              <PrimaryRow
                v-model="primaryInfo"
                :disabled="disabled"
                :module-name="moduleName"
              />
            </slot>
            <slot name="secondary-row">
              <SecondaryRow
                v-model="secondaryInfo"
                :disabled="disabled"
                :module-name="moduleName"
                :resource="resource"
                :is-geolocation-supported="isGeolocationSupported"
              />
            </slot>
            <slot name="tags-row">
              <TagsRow
                v-model="tagsInfo"
                :disabled="disabled"
                :module-name="moduleName"
                :resource="resource"
                :is-approval="isApproval"
                :approval="approval"
                @identifiedAsProblem="$emit('identifiedAsProblem')"
              />
            </slot>
            <slot v-if="allowAttachments" name="attachments">
              <FlotoAttachment
                :disabled="disabled"
                :value="resource.fileAttachments"
                only-list
                @change="updateResource({ fileAttachments: $event })"
              />
            </slot>
            <slot name="additional-fields">
              <!-- <AdditionalInfoWithExpand
                :resource="resource"
                :module-name="$constants.ASSET"
                :show-no-data="false"
                :disabled="disabled"
                :col-size="additionalInfoColSize"
                :update-fn="handleUpdateMoreDetails"
                :default-expanded="isPortalLogin"
              /> -->
            </slot>
            <slot />
          </MCol>
          <MCol
            v-if="hasSidebar"
            v-bind="sidebarSize"
            class="border border-neutral-lighter sidebar-border border-b-0 border-t-0 border-solid transition-width py-6 relative pages-inner-right-slide-panel"
            style="min-height: 120px"
          >
            <slot v-if="sidebarVisible" name="sidebar">
              <div class="overflow-x-hidden">
                <Sidebar
                  v-model="sidebarInfo"
                  :disabled="disabled"
                  :module-name="moduleName"
                />
              </div>
            </slot>
            <div class="toggle">
              <a
                href="javascript:;"
                class="text-neutral hover:text-neutral"
                @click="sidebarVisible = !sidebarVisible"
              >
                <MIcon :name="`chevron-${isRtl ? sidebarRtl : sidebarMain}`" />
              </a>
            </div>
          </MCol>
        </MRow>
      </MCol>
    </FlotoScrollView>
    <FlotoDrawerForm
      v-if="!disabled"
      :open="isEditingSubject"
      @cancel="isEditingSubject = false"
      @submit="handleUpdateSubject"
    >
      <template v-slot:header>
        {{ $t('edit') }}:
        {{ moduleName === $constants.CMDB ? resource.ciName : resource.name }}
      </template>
      <FlotoFormItem
        v-if="moduleName === $constants.CMDB"
        v-model="internalValueModel.ciDisplayName"
        :label="titleLabel"
        :placeholder="titleLabel"
        rules="required"
      />
      <FlotoFormItem
        v-else
        v-model="internalValueModel.displayName"
        :label="titleLabel"
        :placeholder="titleLabel"
        rules="required"
      />
      <FlotoFormItem
        v-if="!allowedFields || allowedFields.indexOf('description') >= 0"
        :label="descriptionLabel"
        rules="description"
      >
        <FlotoRichEditor
          v-model="internalValueModel.description"
          :placeholder="descriptionLabel"
          :affix-toolbar="false"
        />
      </FlotoFormItem>
      <FlotoFormItem v-if="allowAttachments" :label="$tc('attachment', 2)">
        <FlotoAttachment
          v-model="internalValueModel.fileAttachments"
          @upload-start="updatingSubject = true"
          @upload-done="updatingSubject = false"
        />
      </FlotoFormItem>
      <template v-slot:actions="{ submit, hide }">
        <MButton :loading="updatingSubject" @click="submit">
          {{ $t('update') }}
        </MButton>
        <MButton variant="default" class="ml-2" @click="hide">
          {{ $t('cancel') }}
        </MButton>
      </template>
    </FlotoDrawerForm>
  </FlotoFixedView>
</template>

<script>
import IsEqual from 'lodash/isEqual'
import Pick from 'lodash/pick'
import { authComputed } from '@state/modules/auth'
// import AdditionalInfoWithExpand from '@components/additional-information/additional-info-expandable'
import PrimaryRow from './primary-row'
import SecondaryRow from './secondary-row'
import TagsRow from './tags-row'
import Sidebar from './sidebar'

export default {
  name: 'ResourceDetail',
  components: {
    PrimaryRow,
    SecondaryRow,
    TagsRow,
    Sidebar,
    // AdditionalInfoWithExpand,
  },
  props: {
    disabled: { type: Boolean, default: false },
    resource: { type: Object, required: true },
    isApproval: { type: Boolean, default: false },
    approval: { type: Object, default: undefined },
    // eslint-disable-next-line
    allowAttachments: { type: Boolean, default: true },
    // eslint-disable-next-line
    hasSidebar: { type: Boolean, default: true },
    // eslint-disable-next-line
    showRequester: { type: Boolean, default: true },
    moduleName: { type: String, default: 'asset_hardware' },
    updateApi: { type: Function, required: true },
    sidebarWidth: { type: Object, default: () => ({ size: 3 }) },
    detailWidth: { type: Object, default: () => ({ size: 9 }) },
    defaultSidebarVisible: { type: Boolean, default: false },
    allowedFields: { type: Array, default: undefined },
    titleLabel: {
      type: String,
      default: function () {
        return this.$t('name')
      },
    },
    descriptionLabel: {
      type: String,
      default: function () {
        return this.$t('description')
      },
    },
    isTicketClosed: { type: Boolean, default: false },
    additionalInfoColSize: { type: Number, default: undefined },
    isGeolocationSupported: { type: Boolean, default: false },
  },
  data() {
    this.defaultStyle = {
      height: `${window.outerHeight}px`,
    }
    // @TODO make this as per preference
    return {
      sidebarVisible: this.defaultSidebarVisible,
      isEditingSubject: false,
      updatingSubject: false,
      internalValueModel: {},
    }
  },
  computed: {
    ...authComputed,
    sidebarRtl() {
      return this.sidebarVisible ? 'left' : 'right'
    },
    sidebarMain() {
      return this.sidebarVisible ? 'right' : 'left'
    },
    agentStatus() {
      if (this.resource && this.resource.agentActiveStatus) {
        if (this.resource.agentActiveStatus === 'active') {
          return '#6bda7b'
        }
        if (this.resource && this.resource.agentActiveStatus === 'not_active') {
          return '#ffe000'
        }
        if (
          this.resource &&
          this.resource.agentActiveStatus === 'not_applicable'
        ) {
          return ''
        }
      }
      return ''
    },
    detailSize() {
      if (this.hasSidebar && this.sidebarVisible) {
        return this.detailWidth
      }
      return { size: 12 }
    },
    sidebarSize() {
      if (this.sidebarVisible) {
        return this.sidebarWidth
      }
      return { size: undefined }
    },
    primaryInfo: {
      get() {
        return Pick(this.resource, [
          'model',
          'assetTypeId',
          'ciTypeId',
          'statusId',
          'ciStatusId',
          'impactId',
          'productId',
          'softwareLicenseId',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    secondaryInfo: {
      get() {
        return Pick(this.resource, [
          'id',
          'asset',
          'ci',
          'model',
          'swTypeId',
          'managedByGroupId',
          'managedById',
          'assetGroupId',
          'ciGroupId',
          'softwareLicenseId',
          'defaultLocationName',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    tagsInfo: {
      get() {
        return Pick(this.resource, [
          'model',
          'tags',
          'excludedFromScan',
          'exemptSwProhibit',
          'approvalStatus',
          'ciApprovalStatus',
          'checkerApprovalStatus',
          'varianceDetected',
          'assetCondition',
          'movementStatus',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
    sidebarInfo: {
      get() {
        return Pick(this.resource, [
          'id',
          'version',
          'swCategoryId',
          'locationId',
          'assignedDepartmentId',
          'hostName',
          'domainName',
          'ipAddress',
          'macAddress',
          'subnetMask',
          'vendorId',
          'businessServiceId',
          'origin',
          'underChangeControl',
          'acquitionTime',
          'assignmentTime',
          'createdAt',
          'updatedAt',
          'resourceId',
          'assetCondition',
          'movementStatus',
          'assignedUserIds',
          'lastQRAndBarcodeScanBy',
          'lastQRAndBarcodeScanDate',
          'companyId',
          'computerUuid',
        ])
      },
      set(value) {
        this.updateResource(value)
      },
    },
  },
  methods: {
    handleUpdateMoreDetails(data) {
      const diff = Object.keys(data).filter(
        (k) => !IsEqual(this.resource.fieldValueDetails[k], data[k])
      )
      return this.updateResource({
        ...Pick(data, diff),
        fieldValueDetails: { ...this.resource.fieldValueDetails },
      })
    },
    updateResource(change) {
      return this.updateApi({ id: this.resource.id, ...change })
    },
    handleEditSubject() {
      this.internalValueModel = Pick(this.resource, [
        'displayName',
        'ciDisplayName',
        'description',
        'fileAttachments',
      ])
      this.isEditingSubject = true
    },
    handleUpdateSubject() {
      this.updatingSubject = true
      this.updateApi(this.internalValueModel)
        .then(() => {
          this.internalValueModel = {}
          this.isEditingSubject = false
        })
        .finally(() => (this.updatingSubject = false))
    },
    setInternalValue(change) {
      this.internalValueModel = {
        ...this.internalValueModel,
        ...(change || {}),
      }
    },
  },
}
</script>

<style lang="less" scoped>
.sub-heading {
  @apply flex items-center justify-between;
}

.heading {
  @apply items-start flex-wrap flex flex-col;

  .action {
    line-height: 30px;

    @apply ml-2 flex items-center justify-center absolute right-0 top-0;
  }

  .title {
    @apply overflow-hidden relative;
  }
}

.main-detail-container {
  &.@{ant-prefix}-col-12 {
    width: calc(100% - 34px);
  }
}
</style>
